<script>
import { Helper, debounce } from '@/mixin/helper'
import moment from 'moment'
import * as encryptJwt from '@/services/rest-api-tools/lib/encrypt-jwt/encrypt-jwt/'
import * as moveVehicleCompanyLogApi from '@/services/rest-api-sms/lib/move-vehicle-company-log/move-vehicle-company-log'

export default {
    name: 'SyncMoveVehicleCompanyLog',

    props: {
        modal: {
            type: Boolean,
            required: true,
        },
    },

    mixins: [Helper],

    data() {
        return {
            totalPages: 0,
            isModalOpen: false,
            loading: false,
            filter: '',
            currentPage: 1,
            perPage: 10,
            totalRows: 0,
            pageOptions: [10, 25, 50, 100],
            filteredItems: [],
            fields: [
                { key: 'created_at', label: 'Date Time' },
                { key: 'imei', label: 'IMEI' },
                { key: 'previous_company_domain', label: 'Company Asal' },
                { key: 'previous_vehicle_license', label: 'Kendaraan Asal' },
                { key: 'target_company_domain', label: 'Company Tujuan' },
                { key: 'status', label: 'Status' },
                { key: 'server_region', label: 'Server Region' },
                { key: 'user_name', label: 'User Name' },
            ],
            lastPong: moment(),
            pongInterval: null,
            wsLoading: false,
            connectionKeeper: null,
            lastMessageTime: null,
        }
    },

    watch: {
        modal: {
            immediate: true,
            handler(newValue) {
                this.isModalOpen = newValue
                if (newValue) {
                    this.fetchLogs()
                } else {
                    this.closeWebSocket()
                }
            },
        },
        isModalOpen(newValue) {
            this.$emit('update:modal', newValue)
            if (!newValue) {
                this.closeWebSocket()
            }
        },
    },

    methods: {
        debounceFetchLogs: debounce(function () {
            this.fetchLogs()
        }, 500),

        async fetchLogs() {
            this.loading = true
            const params = {
                page: this.currentPage,
                limit: this.perPage,
                // Jika ada filter/search bisa ditambahkan
                // search: this.filter || undefined,
            }

            try {
                const response = await moveVehicleCompanyLogApi.get(params)

                if (!response?.data?.paginated_result) return

                this.filteredItems = response.data.paginated_result
                this.totalRows = response.metadata.total_count
                this.totalPages = response.metadata.total_pages
                this.currentPage = response.metadata.page

                this.loading = false
            } catch (error) {
                this.$toast.error('Error loading logs')
                this.loading = false
                return
            } finally {
                const itemsToSubscribe = this.filteredItems.filter(
                    (item) => item.status === 'waiting' || item.status === 'inprogress'
                )

                const tags = itemsToSubscribe.map(
                    (item) => `move-vehicle-company-log-${item.id}`
                )

                if (tags.length > 0) {
                    const payload = {
                        payload: { tags },
                    }
                    const encryptedPayload = await encryptJwt.store(payload)
                    localStorage.setItem('payload_move_vehicle', encryptedPayload?.data)
                    this.startWebSocket()
                }

                this.loading = false
            }
        },

        resetModal() {
            this.filter = ''
            this.currentPage = 1
            this.closeWebSocket()
        },

        async closeWebSocket() {
            if (this.connectionKeeper) {
                clearInterval(this.connectionKeeper)
                this.connectionKeeper = null
            }

            if (this.$socket && this.$socket.readyState === WebSocket.OPEN) {
                await this.$store.dispatch('ws/WSDisconnect')
            }

            this.wsLoading = false
            this.lastMessageTime = null
            localStorage.removeItem('payload_move_vehicle')
        },

        handleError(error) {
            // (salin dari komponen asli atau gunakan mixin helper)
            if (error.response) {
                const errorStatus = error.response.status
                const errorData = error.response.data

                if (errorStatus === 400 && errorData.details) {
                    errorData.details.forEach((element) => {
                        this.$toast.error(`${errorData.error}: ${element.field} ${element.message}`)
                    })
                } else if (errorStatus === 401) {
                    this.$toast.error(errorData.error)
                    localStorage.removeItem('token')
                    localStorage.removeItem('tokenAccess')
                    this.$router.push({ name: 'Login' })
                } else if (errorStatus === 403) {
                    this.$toast.error(errorData.error)
                } else {
                    this.$toast.error(errorData.error)
                }
            } else if (error.request) {
                this.$toast.error(error.response.statusText)
            } else {
                this.$toast.error(error.message)
            }
        },

        changePage(e) {
            this.currentPage = e
            this.fetchLogs()
        },

        getStatusClass(status) {
            if (status === 'Success' || status === 'success') return 'text-success'
            if (status === 'Failed' || status === 'failed') return 'text-danger'
            if (status === 'waiting') return 'text-warning'
            return 'text-muted'
        },

        resetFilter(newPerPage) {
            this.perPage = newPerPage
            this.currentPage = 1
            this.fetchLogs()
        },

        async startWebSocket() {
            try {
                const tags = localStorage.getItem('payload_move_vehicle')
                if (!tags) return

                if (this.$socket && this.$socket.readyState === WebSocket.OPEN) {
                    await this.$store.dispatch('ws/WSDisconnect')
                }

                const wsUrl = `${process.env.VUE_APP_WS_URL}/ws?token=${localStorage.getItem('wsToken')}`
                await this.$store.dispatch('ws/WSConnect', wsUrl)

                this.wsLoading = true
                this.lastMessageTime = Date.now()

                this.connectionKeeper = setInterval(() => {
                    const currentTime = Date.now()
                    if (currentTime - this.lastMessageTime > 3000) {
                        const obj = {
                            type: 'update_tags',
                            data: tags,
                        }
                        if (this.$socket?.readyState === WebSocket.OPEN) {
                            this.$socket.sendObj(obj)
                        } else if (tags) {
                            this.startWebSocket()
                        }
                    }
                }, 1000)

                while (this.$socket?.readyState !== WebSocket.OPEN) {
                    await new Promise((resolve) => setTimeout(resolve, 100))
                }

                const obj = {
                    type: 'update_tags',
                    data: localStorage.getItem('payload_move_vehicle'),
                }
                this.$socket.sendObj(obj)

                this.$socket.onmessage = (data) => {
                    this.lastMessageTime = Date.now()
                    const signal = JSON.parse(data.data)

                    if (signal.type === 'PONG') {
                        this.$socket.sendObj(obj)
                        this.lastPong = moment()
                        return
                    }

                    if (signal.type && signal.type.startsWith('move-vehicle-company-log-')) {
                        const itemId = signal.type.replace('move-vehicle-company-log-', '')
                        const filteredIndex = this.filteredItems.findIndex(
                            (item) => item.id === +itemId
                        )
                        if (filteredIndex !== -1) {
                            const newData = [...this.filteredItems]
                            newData[filteredIndex] = { ...newData[filteredIndex], ...signal.payload }
                            this.filteredItems = newData
                        }
                    }
                }
            } catch (error) {
                console.error('WebSocket connection error:', error)
                this.wsLoading = false
            }
        },
    },

    mounted() {
        moment.locale('id')
    },

    beforeDestroy() {
        clearInterval(this.connectionKeeper)
    },

    async destroyed() {
        await this.$store.dispatch('ws/WSDisconnect')
        clearInterval(this.pongInterval)
    },
}
</script>

<template>
    <b-modal v-model="isModalOpen" id="move-vehicle-company-log-modal" title="Move Vehicle Company Log" size="xl"
        :hide-footer="true" @hidden="resetModal">
        <div v-if="loading" class="text-center py-3">
            <b-spinner class="align-middle" small></b-spinner>
            <p class="mt-2">Loading Move Vehicle Company Logs...</p>
        </div>

        <div v-else>
            <div class="table-responsive my-3 border border-1">
                <b-table class="datatables" :items="filteredItems" :fields="fields" :busy="loading" show-empty
                    responsive="sm" hover :empty-text="$t('global.emptyTable')"
                    :empty-filtered-text="$t('global.emptyFilteredTable')">
                    <template #cell(created_at)="data">
                        {{ data.item.created_at ? momentDMHms(data.value) : '-' }}
                    </template>
                    <template #cell(imei)="data">
                        {{ data.value || '-' }}
                    </template>

                    <template #cell(previous_vehicle_license)="data">
                        {{ data.value !== null ? data.value : '-' }}
                    </template>

                    <template #cell(previous_company_domain)="data">
                        {{ data.value !== null ? data.value : '-' }}
                    </template>

                    <template #cell(target_company_domain)="data">
                        {{ data.value !== null ? data.value : '-' }}
                    </template>

                    <template #cell(status)="data">
                        <span :class="getStatusClass(data.value)">
                            {{ data.value || '-' }}
                        </span>
                    </template>

                    <template #cell(server_region)="data">
                        {{ data.value || '-' }}
                    </template>

                    <template #cell(user_name)="data">
                        {{ data.value || '-' }}
                    </template>
                </b-table>
            </div>

            <div class="row">
                <div class="col-sm-12 col-md-6">
                    <div id="tickets-table_length" class="dataTables_length">
                        <label class="d-inline-flex align-items-center">
                            {{ $t('show') }}
                            <b-form-select class="form-select form-select-sm" v-model="perPage" size="sm"
                                :options="pageOptions" @change="resetFilter" />
                            {{ $t('entry') }}. {{ $t('show') }}&nbsp;<strong>{{
                                filteredItems.length > 0 ? (currentPage - 1) * perPage + 1 : 0
                            }}</strong>
                            &nbsp;{{ $t('to') }}&nbsp;<strong>{{
                                currentPage != totalPages
                                    ? currentPage * perPage
                                    : (currentPage - 1) * perPage + filteredItems.length
                            }}</strong>
                            &nbsp;{{ $t('from') }}&nbsp;<strong>{{ totalRows }}</strong>&nbsp;{{ $t('entry') }}.
                        </label>
                    </div>
                </div>
                <div class="col-sm-12 col-md-6">
                    <div class="dataTables_paginate paging_simple_numbers float-end">
                        <ul class="pagination pagination-rounded mb-0">
                            <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage"
                                @change="changePage" />
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </b-modal>
</template>